import { Injectable } from '@angular/core';
import { TableColumnsProp } from '../../models/tables.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  selectionTableNames,
  GridColumnsStorageNames,
  pageType,
  TableProps,
} from '../../utility/constants';
import {
  AccountColumnList,
  AgentColumnList,
  AgentFacilityColumnList,
  ManifestBagsCustomizeColumns,
  bagPieceGridDisplayColumns,
  MawbColumnsList,
  MAWBFlightCustomizeColumns,
  MAWBManifestCustomizeColumns,
  shipmentCommodityColumnList,
  ShipmentGatewayPieceColumnList,
  ShipmentGatewayPieceShortageColumnList,
  statusCodeColumnList,
  smStatusCodesColumnList,
  smRolesAndPermissionsColumnList,
  gatewayMasterShipmentColumnList,
  ShipmentBagColumnList,
  UserManagementColumnList,
  UserDepartmentColumnList,
  UserLoginInformationColumnList,
  customerServiceColumnList,
  AirportsColumnList,
  NumberRangesColumnList,
  AirlinesColumnList,
  CbpRequestStatusColumnList,
  CbpResponseStatusColumnList,
  CustomsCityAbiColumnList,
  CustomsCityDraftColumnList,
  FetchFullmanFilesColumnList,
  bagGatewyColumnList,
  agentTypesColumnList,
  hsCodeColumnList,
  facilitiesColumnList,
  serviceTypesColumnList,
  serviceTypeGroupsColumnList,
  clearanceTypeGroupsColumnList,
  SetRangeColumnList,
  accountUsersColumnList,
  setRangeWithAction,
  linkedAccountColumnList,
  ibcChargeCodeColumnList,
  rateScaleColumnList,
  rsAccessorialManualEntry,
  rsAccessorialCharges,
  deliveryZoneColumnList,
  deliveryZoneInfoColList,
  apChargeCodeColumnList,
  apChargeCodeInfoColList,
  apChargeCodeDetailsColList,
  apSummaryGridColumnList,
  apChargesForApModule,
  shipmentAndMawbArChargesTableColumnList,
  shipmentAndMawbApChargesTableColumnList,
  selectInvoiceFromShipmentColumnList,
  arSummaryTableColumnList,
  arChargeCodeColumnList,
  arChargesForARModule,
  ApSftpFilesColumnList,
  ScanShipmentBag,
  AgentTrackingSelectionColumns,
  MawbPalletCustomizeColumns,
  scannedBagsTableColumnList,
  currencyManagementColumnList,
  currencyDetailsColumnList,
  apDashboardUnpublishedInvoicesTableColumnList,
  apDashboardRecentInvoicesTableColumnList,
  arDashboardInvoicesYetToSendToCustomerTableColumnList,
  arDashboardSpecialShipmentsYetToBeInvoicedTableColumnList,
  routesSelectionTableColumnList,
  hawbFinalDestRoutingConfirmColumnList,
  transitPointsTableColumnList,
  accountRoutingCriteriaColumnList,
  routingColumnList,
  routingRoutesColumnList,
  transitPointsInlineColumnList,
  ARMultiShipmentSelectionColumns,
  AssociateUfoShipmentsColumns,
  ManifestMultipleBagSelectionColumns,
  arDashboardInvoicesYetToSendToGPTableColumnList,
  ManifestPublishAttachmentColumnList,
} from './tables.config';
import { BehaviorSubject, Subject } from 'rxjs';
import { readStoredColumnOrder } from '../../utility/grid-column-preferences.util';

@Injectable({
  providedIn: 'root',
})
export class TableService {
  /* Customize menu selection/deselection in the table */
  public tableDisplayedColumns = new BehaviorSubject<string[]>([]);
  public customizeMenuColumns = new BehaviorSubject<TableColumnsProp[]>([]);
  public reorderedColumns = new BehaviorSubject<string[]>([]);
  public defaultSelectedColumn = new BehaviorSubject<string>('');
  public columnVisibilityChanged = new Subject<void>();
  public handleTableCustomizeCheckboxSelection(
    index: number,
    isSelected: boolean,
    customizeMenuColumns: TableColumnsProp[],
    tableDisplayedColumns: string[],
    orderedColumns: string[],
    isCustomizeAsLast: boolean
  ) {
    if (!isSelected) {
      customizeMenuColumns[index].isSelected = true;
      tableDisplayedColumns.splice(0, 1); // to remove 'select' from array
      tableDisplayedColumns.splice(tableDisplayedColumns.length - 1, 1); // to remove 'action' from array
      tableDisplayedColumns.splice(index, 0, customizeMenuColumns[index].key);
      tableDisplayedColumns.unshift('select'); // to add 'select' as 1st position in array
      isCustomizeAsLast
        ? tableDisplayedColumns.push('customize')
        : tableDisplayedColumns.push('action'); // to add 'action' or 'customize' as last position in array
      tableDisplayedColumns.sort(
        (a, b) => orderedColumns.indexOf(a) - orderedColumns.indexOf(b)
      );
    } else {
      customizeMenuColumns[index].isSelected = false;
    }
    this.columnVisibilityChanged.next();
  }

  /* Drag & drop columns in the table */
  public dragAndDropGridColumns(
    event: CdkDragDrop<string[]>,
    displayedColumns: string[],
    customiseTableColumns: TableColumnsProp[],
    storageName: string,
    isCustomizeAsLast: boolean
  ) {
    moveItemInArray(
      displayedColumns,
      event.previousIndex + 1,
      event.currentIndex + 1
    ); // in built method to reorder columns
    const unselectedColumns = customiseTableColumns.filter(
      column => !displayedColumns.includes(column.key)
    ); // filter out unselected columns
    customiseTableColumns = customiseTableColumns.filter(column =>
      displayedColumns.includes(column.key)
    );
    customiseTableColumns.sort(
      (a, b) =>
        displayedColumns.indexOf(a.key) - displayedColumns.indexOf(b.key)
    );
    customiseTableColumns.push(...unselectedColumns); // after sort adding unselected column at last
    const columnsOrderToSave = customiseTableColumns.map(obj => obj.key);
    columnsOrderToSave.unshift('select'); // to add 'select' as 1st position in array
    isCustomizeAsLast
      ? columnsOrderToSave.push('customize')
      : columnsOrderToSave.push('action'); // to add 'action' as last position in array
    localStorage.setItem(storageName, JSON.stringify(columnsOrderToSave));
    return customiseTableColumns;
  }

  /* get storage name for the table */
  public getStorageName(isFrom: string) {
    const storageNameObj = {
      [pageType.mawb]: GridColumnsStorageNames.mawb,
      [selectionTableNames.MawbManifest]: GridColumnsStorageNames.mawbManifest,
      [selectionTableNames.MawbFlight]: GridColumnsStorageNames.mawbFlight,
      [pageType.agent]: GridColumnsStorageNames.agent,
      [pageType.agentInformation]: GridColumnsStorageNames.agentFacility,
      [pageType.hawb]: GridColumnsStorageNames.hawb,
      [pageType.manifest]: GridColumnsStorageNames.manifest,
      [pageType.bag]: GridColumnsStorageNames.bag,
      [pageType.shipmentCommodity]: GridColumnsStorageNames.shipmentCommodity,
      [pageType.account]: GridColumnsStorageNames.account,
      [pageType.shipmentGatewayPiece]: GridColumnsStorageNames.shipmentGateway,
      [pageType.shipmentGatewayPieceExactMatch]:
        GridColumnsStorageNames.shipmentGateway,
      [pageType.shipmentGatewayPieceShortage]:
        GridColumnsStorageNames.shipmentGatewayPieceShortage,
      [pageType.statusCode]: GridColumnsStorageNames.statusCode,
      [pageType.smStatusCodes]: GridColumnsStorageNames.smStatusCodes,
      [pageType.smRolesAndPermissions]:
        GridColumnsStorageNames.smRolesAndPermissions,
      [pageType.shipmentGatewayMasterShipment]:
        GridColumnsStorageNames.shipmentGatewayMasterShipment,
      [pageType.shipmentBag]: GridColumnsStorageNames.shipmentBag,
      [pageType.userManagement]: GridColumnsStorageNames.smUserManagement,
      [pageType.userDepartment]: GridColumnsStorageNames.smUserDepartment,
      [pageType.userLoginInfo]: GridColumnsStorageNames.smUserLoginInfo,
      [pageType.customerService]: GridColumnsStorageNames.customerService,
      [pageType.smAirports]: GridColumnsStorageNames.smAirports,
      [pageType.smNumberRanges]: GridColumnsStorageNames.smNumberRanges,
      [pageType.smAirlines]: GridColumnsStorageNames.smAirlines,
      [pageType.fetchFullManData]: GridColumnsStorageNames.fetchFullManFileData,
      [pageType.customsCityDraftStatus]: GridColumnsStorageNames.customsCityDraftStatus,
      [pageType.customsCityAbiStatus]: GridColumnsStorageNames.customsCityAbiStatus,
      [pageType.cbpRequestStatus]: GridColumnsStorageNames.cbpRequestStatus,
      [pageType.cbpResponseStatus]: GridColumnsStorageNames.cbpResponseStatus,
      [pageType.bagGateway]: GridColumnsStorageNames.bagGateway,
      [pageType.agentTypes]: GridColumnsStorageNames.agentTypes,
      [pageType.hsCodes]: GridColumnsStorageNames.hsCodes,
      [pageType.facilities]: GridColumnsStorageNames.facilities,
      [pageType.serviceTypes]: GridColumnsStorageNames.serviceTypes,
      [pageType.serviceTypeGroups]: GridColumnsStorageNames.serviceTypeGroups,
      [pageType.clearanceTypes]: GridColumnsStorageNames.clearanceTypes,
      [pageType.accountUsers]: GridColumnsStorageNames.accountUsers,
      [pageType.linkedAccounts]: GridColumnsStorageNames.linkedAccounts,
      [pageType.ibcChargeCodes]: GridColumnsStorageNames.ibcChargeCodes,
      [pageType.deliveryZone]: GridColumnsStorageNames.deliveryZone,
      [pageType.apChargeCodes]: GridColumnsStorageNames.apChargeCodes,
      [pageType.deliveryZone]: GridColumnsStorageNames.deliveryZone,
      [pageType.rateScales]: GridColumnsStorageNames.rateScales,
      [pageType.rateScaleAccessorialUploadSheet]:
        GridColumnsStorageNames.rsAccessorialChartUpload,
      [pageType.accountsReceivables]:
        GridColumnsStorageNames.accountsReceivables,
      [pageType.arChargeCodes]: GridColumnsStorageNames.arChargeCodes,
      [pageType.apChargeCodeSetDetails]:
        GridColumnsStorageNames.apChargesCodeDetail,
      [pageType.accountsPayable]: GridColumnsStorageNames.accountsPayable,
      [pageType.apChargesForApModule]:
        GridColumnsStorageNames.apForAccountsPayablesModule,
      [pageType.arChargesForARModule]:
        GridColumnsStorageNames.arForAccountsReceivablesModule,
      [pageType.arChargesForShipmentMawbModule]:
        GridColumnsStorageNames.arChargesForShipmentMawbModule,
      [pageType.apChargesForShipmentMawbModule]:
        GridColumnsStorageNames.apChargesForShipmentMawbModule,
      [pageType.selectInvoiceFromShipment]:
        GridColumnsStorageNames.selectInvoiceFromShipments,
      [pageType.apSftpFiles]: GridColumnsStorageNames.apSftpFiles,
      [pageType.agentTrackingSelection]:
        GridColumnsStorageNames.agentTrackingSelection,
      [pageType.scannedBagsInManifest]: GridColumnsStorageNames.scannedBagsInManifest,
      [pageType.currencyManagement]: GridColumnsStorageNames.currencyManagement,
      [pageType.apDashboardUnpublishedInvoices]: GridColumnsStorageNames.apDashboardUnpublishedInvoices,
      [pageType.apDashboardRecentInvoices]: GridColumnsStorageNames.apDashboardRecentInvoices,
      [pageType.arDashboardInvoicesYetToSendToGP]: GridColumnsStorageNames.arDashboardInvoicesYetToSendToGP,
      [pageType.arDashboardInvoicesYetToSendToCustomer]: GridColumnsStorageNames.arDashboardInvoicesYetToSendToCustomer,
      [pageType.arDashboardSpecialShipmentsYetToBeInvoiced]: GridColumnsStorageNames.arDashboardSpecialShipmentsYetToBeInvoiced,
      [pageType.hawbRoutesSelection]: GridColumnsStorageNames.hawbRoutesSelection,
      [pageType.hawbFinalDestRoutingConfirm]:
        GridColumnsStorageNames.hawbFinalDestRoutingConfirm,
      [pageType.routing]: GridColumnsStorageNames.routing,
      [pageType.routingRoutes]: GridColumnsStorageNames.routingRoutes,
      [pageType.transitPointsInline]: GridColumnsStorageNames.transitPointsInline,
      [pageType.arMultiShipmentSelection]: GridColumnsStorageNames.arMultiShipmentSelection,
      [pageType.associateUfoShipments]: GridColumnsStorageNames.associateUfoShipments,
      [pageType.manifestMultipleBagSelection]:
        GridColumnsStorageNames.manifestMultipleBagSelection,
      [pageType.manifestPublishAttachments]: GridColumnsStorageNames.manifestPublishAttachments,
    };
    return storageNameObj[isFrom] || '';
  }

  /* get default display columns for the table */
  public getDefaultDisplayColumns(isFrom: string) {
    let defaultDisplayColumns: string[] = [];
    const mapPageToColumn = {
      [pageType.agent]: AgentColumnList,
      [pageType.agentInformation]: AgentFacilityColumnList,
      [pageType.account]: AccountColumnList,
      [pageType.shipmentGatewayPiece]: ShipmentGatewayPieceColumnList,
      [pageType.shipmentGatewayPieceExactMatch]: ShipmentGatewayPieceColumnList,
      [pageType.shipmentGatewayPieceShortage]:
        ShipmentGatewayPieceShortageColumnList,
      [pageType.statusCode]: statusCodeColumnList,
      [pageType.smStatusCodes]: smStatusCodesColumnList,
      [pageType.smRolesAndPermissions]: smRolesAndPermissionsColumnList,
      [pageType.shipmentGatewayMasterShipment]: gatewayMasterShipmentColumnList,
      [pageType.shipmentBag]: ShipmentBagColumnList,
      [pageType.userManagement]: UserManagementColumnList,
      [pageType.userDepartment]: UserDepartmentColumnList,
      [pageType.userLoginInfo]: UserLoginInformationColumnList,
      [pageType.customerService]: customerServiceColumnList,
      [pageType.smAirports]: AirportsColumnList,
      [pageType.smNumberRanges]: NumberRangesColumnList,
      [pageType.smAirlines]: AirlinesColumnList,
      [pageType.fetchFullManData]: FetchFullmanFilesColumnList,
      [pageType.customsCityDraftStatus]: CustomsCityDraftColumnList,
      [pageType.customsCityAbiStatus]: CustomsCityAbiColumnList,
      [pageType.cbpRequestStatus]: CbpRequestStatusColumnList,
      [pageType.cbpResponseStatus]: CbpResponseStatusColumnList,
      [pageType.bagGateway]: bagGatewyColumnList,
      [pageType.agentTypes]: agentTypesColumnList,
      [pageType.hsCodes]: hsCodeColumnList,
      [pageType.facilities]: facilitiesColumnList,
      [pageType.serviceTypes]: serviceTypesColumnList,
      [pageType.serviceTypeGroups]: serviceTypeGroupsColumnList,
      [pageType.clearanceTypes]: clearanceTypeGroupsColumnList,
      [pageType.numberRangesSetRange]: SetRangeColumnList,
      [pageType.accountUsers]: accountUsersColumnList,
      [pageType.numberRangesEditSetRange]: setRangeWithAction,
      [pageType.linkedAccounts]: linkedAccountColumnList,
      [pageType.ibcChargeCodes]: ibcChargeCodeColumnList,
      [pageType.rateScales]: rateScaleColumnList,
      [pageType.rateScaleAccessorialManualEntry]: rsAccessorialManualEntry,
      [pageType.rateScaleAccessorialUploadSheet]: rsAccessorialCharges,
      [pageType.deliveryZone]: deliveryZoneColumnList,
      [pageType.deliveryZoneInfoTable]: deliveryZoneInfoColList,
      [pageType.apChargeCodes]: apChargeCodeColumnList,
      [pageType.apChargeCodeInfoTable]: apChargeCodeInfoColList,
      [pageType.accountsReceivables]: arSummaryTableColumnList,
      [pageType.arChargeCodes]: arChargeCodeColumnList,
      [pageType.apChargeCodeSetDetails]: apChargeCodeDetailsColList,
      [pageType.accountsPayable]: apSummaryGridColumnList,
      [pageType.apChargesForApModule]: apChargesForApModule,
      [pageType.arChargesForARModule]: arChargesForARModule,
      [pageType.arChargesForShipmentMawbModule]:
        shipmentAndMawbArChargesTableColumnList,
      [pageType.apChargesForShipmentMawbModule]:
        shipmentAndMawbApChargesTableColumnList,
      [pageType.selectInvoiceFromShipment]: selectInvoiceFromShipmentColumnList,
      [pageType.apSftpFiles]: ApSftpFilesColumnList,
      [pageType.scanShipmentBag]: ScanShipmentBag,
      [pageType.agentTrackingSelection]: AgentTrackingSelectionColumns,
      [pageType.scannedBagsInManifest]: scannedBagsTableColumnList,
      [pageType.currencyManagement]: currencyManagementColumnList,
      [pageType.currencyDetails]: currencyDetailsColumnList,
      [pageType.apDashboardUnpublishedInvoices]: apDashboardUnpublishedInvoicesTableColumnList,
      [pageType.apDashboardRecentInvoices]: apDashboardRecentInvoicesTableColumnList,
      [pageType.arDashboardInvoicesYetToSendToGP]: arDashboardInvoicesYetToSendToGPTableColumnList,
      [pageType.arDashboardInvoicesYetToSendToCustomer]: arDashboardInvoicesYetToSendToCustomerTableColumnList,
      [pageType.arDashboardSpecialShipmentsYetToBeInvoiced]: arDashboardSpecialShipmentsYetToBeInvoicedTableColumnList,
      [pageType.hawbRoutesSelection]: routesSelectionTableColumnList,
      [pageType.hawbFinalDestRoutingConfirm]:
        hawbFinalDestRoutingConfirmColumnList,
      [pageType.hawbTransitPoints]: transitPointsTableColumnList,
      [pageType.accountRoutingCriteria]: accountRoutingCriteriaColumnList,
      [pageType.routing]: routingColumnList,
      [pageType.routingRoutes]: routingRoutesColumnList,
      [pageType.transitPointsInline]: transitPointsInlineColumnList,
      [pageType.arMultiShipmentSelection]: ARMultiShipmentSelectionColumns,
      [pageType.associateUfoShipments]: AssociateUfoShipmentsColumns,
      [pageType.manifestMultipleBagSelection]:
        ManifestMultipleBagSelectionColumns,
      [pageType.manifestPublishAttachments]: ManifestPublishAttachmentColumnList,
    };
    if (isFrom === pageType.mawb) {
      defaultDisplayColumns = MawbColumnsList;
    } else if (isFrom === selectionTableNames.MawbManifest) {
      defaultDisplayColumns = MAWBManifestCustomizeColumns;
    } else if (isFrom === selectionTableNames.MawbFlight) {
      defaultDisplayColumns = MAWBFlightCustomizeColumns;
    } else if (isFrom === selectionTableNames.mawbPallet) {
      defaultDisplayColumns = MawbPalletCustomizeColumns;
    } else if (isFrom === pageType.shipmentCommodity) {
      defaultDisplayColumns = shipmentCommodityColumnList;
    } else if (isFrom === selectionTableNames.ManifestBag) {
      defaultDisplayColumns = ManifestBagsCustomizeColumns;
    } else if (isFrom === pageType.bagShipment) {
      defaultDisplayColumns = bagPieceGridDisplayColumns;
    } else {
      defaultDisplayColumns = mapPageToColumn[isFrom];
    }

    return defaultDisplayColumns;
  }

  /* get from local storage */
  public getFromLocalStorage(isFrom: string) {
    const defaults = this.getDefaultDisplayColumns(isFrom);
    const raw = localStorage.getItem(this.getStorageName(isFrom));
    if (!raw) {
      return defaults;
    }
    try {
      return readStoredColumnOrder(JSON.parse(raw), defaults);
    } catch {
      return defaults;
    }
  }

  /* set to local storage */
  public setToLocalStorage(storageName: string, displayColumns: any) {
    localStorage.setItem(storageName, displayColumns);
  }

  /* to check for input row in table */
  public rowContainsInput(row: any): boolean {
    return (
      Object.prototype.hasOwnProperty.call(row, TableProps.inputRow) &&
      row.isInputRow
    );
  }

  public inlineEditTableRowContainsInput(row: any): boolean {
    if (Object.hasOwn(row, TableProps.inputRow) && row.isInputRow) {
      return true;
    }
    return (
      Object.prototype.hasOwnProperty.call(row, TableProps.rowDisable) &&
      !row.isRowDisable
    );
  }

  public setTableDisplayedColumns(data: string[]) {
    this.tableDisplayedColumns.next(data);
  }

  public setCustomizeMenuColumns(data: TableColumnsProp[]) {
    this.customizeMenuColumns.next(data);
  }
  public setReorderedColumns(data: string[]) {
    this.reorderedColumns.next(data);
  }
  public setDefaultTableColumn(data: string) {
    this.defaultSelectedColumn.next(data);
  }
}
